import {useTranslation} from "react-i18next";
import Modal, {SimpleModalControls} from "../../components/Modal";
import {Checkbox, FormControl, FormLabel, Input, Select, Textarea} from "@chakra-ui/react";
import {useState, useEffect} from "react";
import {getAxiosInstance} from "../../utils/axiosInstance";
import axios from "axios";
import {toast} from "react-toastify";
import AddCategory from "./components/AddCategory";
import {Product} from "./product.type";

interface EditPriceDetailsModal extends SimpleModalControls {
    product: Product;
}

type MeasurementUnits = {
    id: string;
    name: string;
}

type Category = {
    value: string;
    label: string;
    colorScheme: string;
    isMain: boolean;
}

type ProductUpdate = {
    id: string;
    name: string;
    description: string;
    sku: string;
    minQuantity: number;
    unitMeasurementId: string;
    productWorkPrice: number;
    productProgramingWorkPrice: number | null;
    categoriesIds?: string[];
    hasSerialNumber: boolean;
}

const ModalEditProductDetails = (props: EditPriceDetailsModal) => {
    const {t} = useTranslation("translations");
    const [measurements, setMeasurements] = useState<MeasurementUnits[]>([]);
    const [product, setProducts] = useState<Product>(props.product);
    const [productUpdate, setProductUpdate] = useState<ProductUpdate>({
        id: product.id,
        name: product.name,
        description: product.description,
        sku: product.sku,
        minQuantity: product.minQuantity,
        unitMeasurementId: product.unitMeasurementId,
        productWorkPrice: product.productWorkPrice !== null ? product.productWorkPrice : 0,
        productProgramingWorkPrice: product.productProgramingWorkPrice,
        categoriesIds: [],
        hasSerialNumber: product.hasSerialNumber
    });
    const [categories, setCategories] = useState<Category[]>([]);


    const unitsOfMeasure = async () => {
        try {
            const res = await getAxiosInstance().get("/UnitsOfMeasure");
            if (res.status === 200) {
                setMeasurements(res.data);
            }
        } catch (error) {
            toast.error("Could not retrieve measurement units", {position: toast.POSITION.BOTTOM_RIGHT});
        }
    }

    const updateProduct = async () => {
        try {

            const res = await getAxiosInstance().put("/Products",
                {
                    ...productUpdate,
                    categoriesIds: categories.map((x) => x.value)
                });

            if (res.status === 200) {
                toast.success("Successful", {position: toast.POSITION.BOTTOM_RIGHT});
                props.onClose();
            }

        } catch (error) {
            if (axios.isAxiosError(error)) {
                toast.error(`${error.response?.data}`, {position: toast.POSITION.BOTTOM_RIGHT})
            } else {
                toast.error(`Could not update the product from the database ${error}`, {position: toast.POSITION.BOTTOM_RIGHT});
            }

        }
    }

    const setUpCategory = () => {
        let arr: Category[] = [];
        for (let cat of props.product.categories as any) {
            arr.push({value: cat.id, label: cat.name, colorScheme: "yellow", isMain: cat.parentId === null});
        }
        setCategories(arr);
    }

    useEffect(() => {
        unitsOfMeasure();
        setUpCategory();

    }, [])

    return (
        <>
            <Modal
                header={t("Edit Product Details")}
                isOpen={props.isOpen}
                onClose={props.onClose}
                secondaryActionName={t("Save")}
                secondaryActionEnabled={true}
                secondaryAction={() => {
                    updateProduct()
                }}
            >
                <>
                    <FormControl>
                        <FormLabel>
                            {t("Name")}
                        </FormLabel>
                        <Input
                            isRequired
                            name='name'
                            value={productUpdate?.name}
                            onChange={(e) => {
                                if (productUpdate) setProductUpdate({
                                    ...productUpdate,
                                    [e.target.name]: e.target.value
                                })
                            }}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel> {t("Description")} </FormLabel>
                        <Textarea
                            isRequired
                            name='description'
                            value={productUpdate?.description}
                            onChange={(e) => {
                                if (productUpdate) setProductUpdate({...productUpdate, [e.target.name]: e.target.value})
                            }}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel> {t("SKU")} </FormLabel>
                        <Input
                            isRequired
                            name='sku'
                            value={productUpdate?.sku}
                            onChange={(e) => {
                                if (product) setProductUpdate({...productUpdate, [e.target.name]: e.target.value})
                            }}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel> {t("MIN_QUANTITY")} </FormLabel>
                        <Input
                            isRequired
                            name='minQuantity'
                            value={productUpdate?.minQuantity}
                            onChange={(e) => {
                                if (product) setProductUpdate({
                                    ...productUpdate,
                                    [e.target.name]: Number(e.target.value)
                                })
                            }}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel>{t("PRODUCT_WORK_PRICE")}</FormLabel>
                        <Input
                            isRequired
                            name="productWorkPrice"
                            type="number"
                            inputMode="numeric"
                            step="0.01"
                            placeholder="ex: 200"
                            value={productUpdate?.productWorkPrice}
                            onChange={(e) => setProductUpdate({...productUpdate, productWorkPrice: +e.target.value})}
                        />
                    </FormControl>
                    <div className="flex flex-row my-2">
                        <Checkbox
                            isChecked={productUpdate.productProgramingWorkPrice !== null}
                            onChange={() => setProductUpdate({
                                ...productUpdate,
                                productProgramingWorkPrice: productUpdate.productProgramingWorkPrice !== null ? null : 0
                            })}
                        />
                        <FormLabel className="ml-2 mt-[6px]"> {t("HAS_PROGRAMING_PRICE")} </FormLabel>
                    </div>

                    {
                        productUpdate.productProgramingWorkPrice !== null &&
                        <FormControl>
                            <FormLabel>{t("PRODUCT_PROGRAMING_WORK_PRICE_RATE")}</FormLabel>
                            <Input
                                isRequired
                                name="productProgramingWorkPrice"
                                type="number"
                                inputMode="numeric"
                                step="0.01"
                                placeholder="ex: 0.2"
                                value={productUpdate.productProgramingWorkPrice}
                                onChange={(e) => setProductUpdate({
                                    ...productUpdate,
                                    productProgramingWorkPrice: +e.target.value
                                })}
                            />
                        </FormControl>
                    }
                    <FormControl>
                        <FormLabel> {t("Unit of Measurement")} </FormLabel>
                        <Select
                            name="unitMeasurementId"
                            defaultValue={productUpdate?.unitMeasurementId}
                            onChange={(e) => {
                                if (productUpdate) setProductUpdate({...productUpdate, [e.target.name]: e.target.value})
                            }}
                            placeholder={t('SELECT_OPTION')}
                        >
                            {
                                measurements.map((c: MeasurementUnits, index: number) => {
                                    return (
                                        <>
                                            <option key={index} id={c.id} value={c.id}>  {c.name} </option>
                                        </>
                                    )
                                })
                            }
                        </Select>
                    </FormControl>
                    <FormControl className="flex flex-row my-2 mt-4">
                        <Checkbox
                            isDisabled={true}
                            className="mr-[5px]"
                            isChecked={productUpdate?.hasSerialNumber}
                            onChange={() => {
                                let tmp = {...productUpdate};
                                tmp.hasSerialNumber = !tmp.hasSerialNumber;
                                setProductUpdate(tmp);
                            }}
                        />
                        <FormLabel className="mt-[5px]">{t("PRODUCT_WITH_SERIAL_NUMBER")}</FormLabel>
                    </FormControl>
                    <FormControl>
                        <FormLabel> {t("Categories")} </FormLabel>
                        <AddCategory
                            selectedCategory={categories}
                            setSelectedCategory={(value) => {
                                setCategories(value)
                            }}
                        />
                    </FormControl>
                </>
            </Modal>
        </>
    )
}

export default ModalEditProductDetails;